import type { VirtuosoHandle } from 'react-virtuoso';

interface VirtuosoEntry {
  handle: VirtuosoHandle | null;
  element: HTMLElement | null;
  atBottom: boolean;
}

const registry = new Map<string, VirtuosoEntry>();

function getOrCreate(id: string): VirtuosoEntry {
  let entry = registry.get(id);
  if (!entry) {
    entry = { handle: null, element: null, atBottom: true };
    registry.set(id, entry);
  }
  return entry;
}

export function registerVirtuoso(id: string, handle: VirtuosoHandle) {
  getOrCreate(id).handle = handle;
}

export function unregisterVirtuoso(id: string, handle: VirtuosoHandle) {
  const entry = registry.get(id);
  if (entry && entry.handle === handle) {
    registry.delete(id);
  }
}

export function setVirtuosoElement(id: string, element: HTMLElement | null) {
  if (element) {
    getOrCreate(id).element = element;
    return;
  }
  const entry = registry.get(id);
  if (entry) {
    entry.element = null;
  }
}

export function setVirtuosoAtBottom(id: string, atBottom: boolean) {
  const entry = registry.get(id);
  if (entry) {
    entry.atBottom = atBottom;
  }
}

export function getVirtuosoEntry(id: string): VirtuosoEntry | undefined {
  return registry.get(id);
}

export function isVirtuosoAtBottom(id: string, threshold = 600): boolean {
  const entry = registry.get(id);
  if (!entry) {
    return false;
  }
  const el = entry.element;
  if (el) {
    return el.scrollHeight - el.offsetHeight - el.scrollTop < threshold;
  }
  return entry.atBottom;
}

export function scrollVirtuosoToBottom(
  id: string,
  behavior: 'auto' | 'smooth' = 'auto',
): boolean {
  const entry = registry.get(id);
  if (!entry || !entry.handle) {
    return false;
  }

  entry.handle.scrollToIndex({ index: 'LAST', align: 'end', behavior });

  // Virtuoso's scrollToIndex aligns the last data item; nudge past it so
  // footer content (Assistant / subscription CTA) is revealed too.
  const revealFooter = () => {
    if (entry.element) {
      entry.element.scrollTop = entry.element.scrollHeight;
    }
  };
  revealFooter();
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(revealFooter);
  }
  return true;
}
